// Progress update on the task page (plan §4 PATCH /tasks/:taskId, M5-R3): the update notes, the
// actual finish and the percent done. The task's people and the co-supervisors edit the notes and
// the finish date; only the owner overrides the percent (spec §5.1). Each edit opens a dialog on
// the values saved at that moment and sends only the fields the person changed.
import { useId, useState, type FormEvent } from 'react';
import { LEADS_REDACTION, today } from '@cis/shared';
import type { TaskDetail } from '@cis/api-client';
import type { TaskEdit } from '../../api/tasks.ts';
import { Modal } from '../../ui/modal.tsx';
import { DialogActions, ProblemText } from './dialog-parts.tsx';
import { formatDate } from './format.ts';
import { PercentDialog } from './percent-override.tsx';
import { baseFor, type TaskWriter } from './use-task-writer.ts';

const NOTES_MAX = 2000;
const edited = (d: TaskDetail): unknown => [d.state.updateNotes, d.state.actualFinish];

/** The fields that differ from what `saved` holds; null when nothing changed. */
export function editOf(saved: TaskDetail, notes: string, finish: string): TaskEdit | null {
  const savedNotes = saved.state.updateNotes ?? '';
  const savedFinish = saved.state.actualFinish ?? '';
  const edit: TaskEdit = {
    ...(notes.trim() === savedNotes ? {} : { updateNotes: notes.trim() === '' ? null : notes }),
    ...(finish === savedFinish ? {} : { actualFinish: finish === '' ? null : finish }),
  };
  return Object.keys(edit).length === 0 ? null : edit;
}

function UpdateDialog(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly leadsReader: boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { detail, writer } = props;
  const notesId = useId();
  const finishId = useId();
  const [opened] = useState(detail);
  const [notes, setNotes] = useState(opened.state.updateNotes ?? '');
  const [finish, setFinish] = useState(opened.state.actualFinish ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  // A team role reads Leads ids redacted; saving would write the marker over them (the API refuses).
  const hidden = !props.leadsReader && (opened.state.updateNotes ?? '').includes(LEADS_REDACTION);
  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const edit = editOf(opened, notes, finish);
    if (edit === null) {
      setProblem('Nothing has changed yet.');
      return;
    }
    const outcome = await writer.send({ kind: 'edit', edit }, baseFor(opened, detail, edited));
    if (outcome === 'ok' || outcome === 'conflict') props.onClose();
    if (outcome === 'failed') setProblem(writer.lastError());
  };
  return (
    <Modal title="Update the task" onClose={props.onClose} busy={writer.pending}>
      <form className="field" onSubmit={(e) => void onSubmit(e)}>
        <label className="field__label" htmlFor={notesId}>
          Update notes
        </label>
        <textarea
          id={notesId}
          className="field__input"
          rows={5}
          maxLength={NOTES_MAX}
          readOnly={hidden}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {hidden && (
          <p className="card__hint">
            Parts of these notes are only for the Leads, so only the owner or the supervisor can
            change them.
          </p>
        )}
        <label className="field__label" htmlFor={finishId}>
          Actual finish
        </label>
        <input
          id={finishId}
          type="date"
          className="field__input"
          max={today()}
          value={finish}
          onChange={(e) => setFinish(e.target.value)}
        />
        <ProblemText problem={problem} />
        <DialogActions writer={writer} onCancel={props.onClose} submitLabel="Save update" />
      </form>
    </Modal>
  );
}

function UpdateFacts(props: { readonly detail: TaskDetail }): React.JSX.Element {
  const { state } = props.detail;
  return (
    <dl className="facts">
      <dt>Update notes</dt>
      <dd className="step__text">{state.updateNotes ?? 'None yet'}</dd>
      <dt>Actual finish</dt>
      <dd>{formatDate(state.actualFinish)}</dd>
      <dt>Percent done</dt>
      <dd>
        {state.percent}%
        {state.percentOverrideNote === null
          ? ' (from the required steps ticked)'
          : ` (set by the owner: ${state.percentOverrideNote})`}
      </dd>
    </dl>
  );
}

type Open = 'update' | 'percent' | null;

/** "Progress update" card: the saved values, and the edits this person may make. */
export function TaskUpdate(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly leadsReader: boolean;
  readonly readOnly: boolean;
}): React.JSX.Element {
  const { detail, writer } = props;
  const [open, setOpen] = useState<Open>(null);
  const { edit, overridePercent } = detail.permissions;
  const close = (): void => setOpen(null);
  return (
    <section className="card" aria-labelledby="update-title">
      <h2 className="card__title" id="update-title">
        Progress update
      </h2>
      <UpdateFacts detail={detail} />
      {!props.readOnly && (edit || overridePercent) && (
        <div className="form-row" role="group" aria-label="Progress update actions">
          {edit && (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={writer.pending}
              onClick={() => setOpen('update')}
            >
              Edit the update
            </button>
          )}
          {overridePercent && (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={writer.pending}
              onClick={() => setOpen('percent')}
            >
              Change percent done
            </button>
          )}
        </div>
      )}
      {open === 'update' && (
        <UpdateDialog
          detail={detail}
          writer={writer}
          leadsReader={props.leadsReader}
          onClose={close}
        />
      )}
      {open === 'percent' && <PercentDialog detail={detail} writer={writer} onClose={close} />}
    </section>
  );
}
